import { useState, useCallback, useRef } from 'react';
import { Activity } from '../types';
import { useFlowClient } from './useFlowClient';

export function useWeeklyData(userEmail: string | null, projectId: string | undefined) {
  const flowClient = useFlowClient();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadedProjectRef = useRef<string | undefined>(undefined);

  const load = useCallback(async (forceRefresh = false) => {
    if (!userEmail || !projectId) return;
    if (!forceRefresh && loadedProjectRef.current === projectId && activities.length > 0) return;
    setLoading(true);
    setError(null);
    try {
      const res = await flowClient.ask(userEmail, projectId, 'get_project_activities');
      let parsed: Activity[] = [];
      try {
        const d = JSON.parse(res.answer);
        parsed = d.activities || d || [];
      } catch {
        parsed = [];
      }
      setActivities(Array.isArray(parsed) ? parsed : []);
      loadedProjectRef.current = projectId;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activities.');
    } finally {
      setLoading(false);
    }
  }, [flowClient, userEmail, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateActivity = useCallback((activityId: string, changes: Partial<Activity>) => {
    setActivities(prev =>
      prev.map(a => a.activity_id === activityId ? { ...a, ...changes } : a)
    );
  }, []);

  return { activities, loading, error, load, updateActivity };
}
